import { createContext, type ReactNode, useEffect, useMemo } from "react"

// Запас над exit-transition (0.3s в styles.css): в фоновой вкладке CSS-transition
// может не завершиться и onOpenChangeComplete не придёт — хост форсирует
// завершение exit-фазы сам. Колбэки потребителей идемпотентны.
const EXIT_SAFETY_TIMEOUT_MS = 700

export type OverlayHostState = {
	/** false — фаза closing: потребитель ещё смонтирован, оверлей играет exit-анимацию. */
	open: boolean
	/** Хост (очередь) узнаёт, что exit доиграл и потребителя можно размонтировать. */
	onExitComplete?: () => void
}

// Дефолт {open: true} — standalone-оверлеи вне очередей работают без провайдера,
// их видимостью рулит собственный проп open.
export const OverlayHostContext = createContext<OverlayHostState>({
	open: true,
})

function OverlayHost({
	open,
	onExitComplete,
	children,
}: OverlayHostState & { children: ReactNode }) {
	const value = useMemo(() => {
		const state: OverlayHostState = { open }
		if (onExitComplete) state.onExitComplete = onExitComplete
		return state
	}, [open, onExitComplete])

	useEffect(() => {
		if (open) return
		const timer = setTimeout(() => onExitComplete?.(), EXIT_SAFETY_TIMEOUT_MS)
		return () => clearTimeout(timer)
	}, [open, onExitComplete])

	return (
		<OverlayHostContext.Provider value={value}>
			{children}
		</OverlayHostContext.Provider>
	)
}

export { OverlayHost }
